import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { Term } from "./Term";

// Connecting a Google Drive, from the console.
//
// WHY IT IS HERE AND NOT IN A SETTINGS PAGE. The connection exists so that "check for new student
// work" can read a folder. When it is missing, that button is where a teacher finds out — so the
// thing that fixes it belongs beside the thing that failed, not two screens away under a gear.
//
// WHAT IT SAYS WHEN IT IS FINE: as little as possible. A connected Drive is a quiet line naming
// the account, because the account matters — in a pilot the Google account and the console
// account are routinely different people, and a teacher who has connected the wrong one should be
// able to see that without clicking anything.
//
// RECONNECTING IS NORMAL, NOT AN ERROR. On an External/Testing consent screen Google expires a
// refresh token after seven days, so a pilot meets this weekly. It reads as a thing to do rather
// than as a fault, because it is.

interface Connection {
  connected: boolean;
  configured: boolean;
  google_email?: string;
  connected_at?: string;
  last_used_at?: string | null;
  needs_reconnect?: boolean;
  failure_detail?: string | null;
  missing_scopes?: string[];
  scopes?: string[];
}

// THE HANDOFF, read once as this module loads.
//
// Google sends the person back to /writing?drive=connected&handoff=… and the app normalises its
// own URL as soon as it routes, so by the time a component renders the query is gone. Reading it
// at module scope catches it while it is still there. The value is a signed blob naming the
// person the flow was started by; posting it is what actually records the connection, because the
// callback that produced it had no authenticated session to write with.
const RETURNED = (() => {
  if (typeof window === "undefined") return { status: "", handoff: "" };
  const q = new URLSearchParams(window.location.search);
  return { status: q.get("drive") ?? "", handoff: q.get("handoff") ?? "" };
})();

/** What came back from Google, in a teacher's words. `connected` is not here: it is not a message,
 *  it is the state the panel will be showing anyway. */
const RETURN_MESSAGE: Record<string, string> = {
  declined: "You did not finish connecting Google Drive, so nothing was changed.",
  stale: "That connection attempt timed out. Connect again.",
  // "Student Writing" is the app's name on its Google OAuth consent screen, which is what the
  // person will find on that page. It changes here when the consent screen is renamed, not before.
  no_refresh_token: "Google did not offer this account again. Remove Student Writing at "
    + "myaccount.google.com/permissions, then connect once more.",
  failed: "Google Drive could not be connected. Nothing was changed.",
};

export function DriveConnection({ onChanged }: { onChanged?: () => void }) {
  const [state, setState] = useState<Connection | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string>(RETURN_MESSAGE[RETURNED.status] ?? "");
  const [asked, setAsked] = useState(false);
  // THE RETURN LEG RUNS ONCE. `RETURNED` is a module constant, so an effect that re-ran would
  // post the same handoff again on every render of the screen above this one — and with `busy`
  // set each time, every control here stayed disabled forever. The preview found it: Disconnect
  // could not be pressed.
  const started = useRef(false);
  // Held in a ref so the effect does not depend on a prop whose identity changes every render.
  const changed = useRef(onChanged);
  changed.current = onChanged;

  const read = useCallback(() => {
    api.get<Connection>("/drive/connection")
      .then(setState)
      // A connection that cannot be read is not a connection that is absent, but the screen can
      // only offer to connect either way.
      .catch(() => setState({ connected: false, configured: true }));
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Finish the flow first, if this load is the return leg: recording the connection and then
    // reading it back means the panel shows the server's answer rather than our assumption.
    if (RETURNED.status === "connected" && RETURNED.handoff) {
      setBusy(true);
      api.post<Connection>("/drive/connection", { handoff: RETURNED.handoff })
        .then((r) => { setState(r); setNote(""); changed.current?.(); })
        .catch((e: unknown) => {
          setError(e instanceof ApiError ? e.message : "that connection could not be recorded");
          read();
        })
        .finally(() => setBusy(false));
      return;
    }
    read();
  }, [read]);

  const connect = () => {
    setBusy(true); setError(null);
    api.post<{ url: string }>("/drive/connect", {})
      .then((r) => { window.location.href = r.url; })
      .catch((e: unknown) => {
        setError(e instanceof ApiError ? e.message : "Google Drive could not be reached");
        setBusy(false);
      });
  };

  const disconnect = () => {
    setBusy(true); setError(null);
    api.del<Connection>("/drive/connection")
      .then(() => { setAsked(false); read(); changed.current?.(); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "could not disconnect"))
      .finally(() => setBusy(false));
  };

  if (!state) return null;

  // Nobody has made an OAuth client for this deployment. A teacher can do nothing about that, so
  // they are not shown a button that will refuse them.
  if (!state.configured) return null;

  const missing = state.missing_scopes ?? [];

  return (
    // QUIET WHEN IT IS WORKING. Connected, with the right scopes, there is nothing here a teacher
    // has to do — it is the answer to "is Drive still hooked up", asked rarely. It was set in the
    // same size as the controls and the account in bold, which put a debug line at the weight of
    // the work. Broken, it goes back to full size, because then it IS the thing to act on.
    <div className={`dc${state.connected && !state.needs_reconnect && !missing.length
                        ? " dc-quiet" : ""}`}>
      {state.connected ? (
        <>
          <span className={`dc-dot${state.needs_reconnect || missing.length ? " dc-warn" : ""}`}
                aria-hidden="true" />
          <span className="dc-who">
            {/* The ACCOUNT, always. Two different people is the ordinary case in a pilot — and it
                is why this cannot be dropped entirely, only turned down. */}
            Drive: {state.google_email}
          </span>
          {state.needs_reconnect && (
            <span className="dc-why">
              {state.failure_detail || "the authorisation expired"} —{" "}
              <button className="btn-link" disabled={busy} onClick={connect}>reconnect</button>
            </span>
          )}
          {missing.length > 0 && !state.needs_reconnect && (
            <span className="dc-why">
              {/* Granted less than was asked for: enumerating works and reading a document
                  does not, which would otherwise surface as a 403 on the first paper. */}
              did not grant {missing.join(" or ")} —{" "}
              <button className="btn-link" disabled={busy} onClick={connect}>connect again</button>
            </span>
          )}
          {!asked ? (
            <button className="btn-link dc-off" disabled={busy}
                    onClick={() => setAsked(true)}>Disconnect</button>
          ) : (
            <span className="dc-why">
              Stop reading this Drive?{" "}
              <button className="btn sm" disabled={busy} onClick={disconnect}>
                {busy ? "Disconnecting…" : "Yes, disconnect"}
              </button>{" "}
              <button className="btn-link" disabled={busy}
                      onClick={() => setAsked(false)}>Cancel</button>
            </span>
          )}
        </>
      ) : (
        <>
          <span className="dc-dot dc-off-dot" aria-hidden="true" />
          {/* WHOSE CONNECTION. A folder is recorded on the collection, so every teacher of the
              cohort sees it; a connection is recorded against one signed-in person and its policy
              shows it to nobody else. So "a folder is attached" and "you have not connected
              Drive" are both true at once, and the screen used to assert the second beside the
              first with no hint that they were about different things. Signing in with a second
              account of your own is enough to see it. */}
          <span className="dc-who">
            You have not <Term name="drive connection">connected Google Drive</Term>
          </span>
          <button className="btn sm" disabled={busy} onClick={connect}>
            {busy ? "Opening Google…" : "Connect Google Drive"}
          </button>
          {/* What they are about to agree to, before they agree to it rather than after. */}
          <span className="dc-why">
            read-only access to the folders you point at. A folder already added to this work
            stays where it is — connecting lets <em>this</em> account read it.
          </span>
        </>
      )}
      {note && <span className="dc-note">{note}</span>}
      {error && <span className="inc-error">{error}</span>}
    </div>
  );
}
